/**
 * TokenRefreshManager - Coordinates token refresh across multiple browser tabs
 *
 * Uses BroadcastChannel API for cross-tab communication and localStorage for
 * leader election to prevent multiple tabs from simultaneously refreshing tokens.
 *
 * Strategy:
 * 1. Each tab monitors token expiration from the accessToken cookie (JWT)
 * 2. When a tab needs to refresh (1 minute before expiration), it tries to acquire a lock
 * 3. Only one tab (the "leader") performs the actual refresh
 * 4. The leader broadcasts the new token to all other tabs via BroadcastChannel
 * 5. Other tabs update their WebSocket connections with the new token
 */

import { getAccessToken } from './auth';

const CHANNEL_NAME = 'token-refresh-channel';
const LOCK_KEY = 'token-refresh-lock';
const LOCK_TIMEOUT_MS = 10000; // 10 seconds lock timeout
const REFRESH_BEFORE_EXPIRY_MS = 60 * 1000; // 1 minute before expiry
const RETRY_DELAY_MS = 30 * 1000; // retry delay after a failed refresh

// Debug logging prefix
const LOG_PREFIX = '[TokenRefresh]';

// Verbose logging (incl. decoded token claims) must never reach production consoles
function debugLog(...args: unknown[]): void {
  if (import.meta.env.DEV) console.log(...args);
}

/**
 * Format milliseconds to human readable string (e.g., "2m 30s")
 */
function formatMs(ms: number): string {
  if (ms < 0) return 'expired';
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  return `${seconds}s`;
}

/**
 * Format date for logging
 */
function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString();
}

interface LockData {
  tabId: string;
  timestamp: number;
}

interface TokenRefreshMessage {
  type: 'TOKEN_REFRESHED' | 'REFRESH_STARTED' | 'REFRESH_FAILED';
  tabId: string;
  accessToken?: string;
  expirationMs?: number;
  timestamp: number;
}

type TokenRefreshCallback = (accessToken: string) => void;

export class TokenRefreshManager {
  private tabId: string;
  private channel: BroadcastChannel | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private onTokenRefreshed: TokenRefreshCallback | null = null;
  private refreshTokenFn: (() => Promise<string | null>) | null = null;
  private isRefreshing = false;
  private lastRefreshAttempt = 0;
  private minRefreshInterval = 5000; // Minimum 5 seconds between refresh attempts

  constructor() {
    this.tabId = this.generateTabId();
    debugLog(`${LOG_PREFIX} Initialized with tabId: ${this.tabId}`);
    this.initBroadcastChannel();
    this.setupVisibilityListener();
  }

  private generateTabId(): string {
    return `tab_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  private initBroadcastChannel(): void {
    if (typeof BroadcastChannel === 'undefined') {
      console.warn(
        `${LOG_PREFIX} BroadcastChannel not supported, falling back to localStorage events`,
      );
      return;
    }

    this.channel = new BroadcastChannel(CHANNEL_NAME);
    this.channel.onmessage = (event: MessageEvent<TokenRefreshMessage>) => {
      this.handleBroadcastMessage(event.data);
    };
    debugLog(`${LOG_PREFIX} BroadcastChannel initialized`);
  }

  private handleBroadcastMessage(message: TokenRefreshMessage): void {
    // Ignore messages from this tab
    if (message.tabId === this.tabId) return;

    debugLog(`${LOG_PREFIX} Received broadcast from ${message.tabId.slice(-6)}:`, message.type);

    switch (message.type) {
      case 'TOKEN_REFRESHED':
        if (message.accessToken && this.onTokenRefreshed) {
          const expiresIn = message.expirationMs ? message.expirationMs - Date.now() : 0;
          debugLog(
            `${LOG_PREFIX} Another tab refreshed token. New expiration: ${
              message.expirationMs ? formatDate(message.expirationMs) : 'unknown'
            } (in ${formatMs(expiresIn)})`,
          );
          // Another tab refreshed the token, update our WebSocket
          this.onTokenRefreshed(message.accessToken);
          // Reschedule our refresh timer based on new expiration
          if (message.expirationMs) {
            this.scheduleRefresh(message.expirationMs);
          }
        }
        break;
      case 'REFRESH_STARTED':
        debugLog(
          `${LOG_PREFIX} Another tab (${message.tabId.slice(-6)}) started refresh, standing by...`,
        );
        // Another tab is refreshing, cancel our attempt if pending
        this.isRefreshing = false;
        break;
      case 'REFRESH_FAILED':
        debugLog(`${LOG_PREFIX} Another tab's refresh failed, may retry`);
        // Another tab's refresh failed, we might try if we're next in line
        break;
    }
  }

  private setupVisibilityListener(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        debugLog(`${LOG_PREFIX} Tab became visible, checking token...`);
        // Tab became visible, check if we need to refresh
        this.checkAndScheduleRefresh();
      }
    });
  }

  /**
   * Try to acquire a lock for token refresh
   * Returns true if lock acquired, false otherwise
   */
  private tryAcquireLock(): boolean {
    const now = Date.now();

    try {
      const existingLock = localStorage.getItem(LOCK_KEY);

      if (existingLock) {
        const lockData: LockData = JSON.parse(existingLock);
        // Check if lock is expired
        if (now - lockData.timestamp < LOCK_TIMEOUT_MS) {
          // Lock is still valid and held by another tab
          debugLog(
            `${LOG_PREFIX} Lock held by ${lockData.tabId.slice(-6)}, age: ${formatMs(
              now - lockData.timestamp,
            )}`,
          );
          return false;
        }
        debugLog(`${LOG_PREFIX} Existing lock expired, taking over`);
      }

      // Try to acquire lock
      const newLock: LockData = {
        tabId: this.tabId,
        timestamp: now,
      };
      localStorage.setItem(LOCK_KEY, JSON.stringify(newLock));

      // Double-check we got the lock (race condition protection)
      const checkLock = localStorage.getItem(LOCK_KEY);
      if (checkLock) {
        const checkData: LockData = JSON.parse(checkLock);
        const acquired = checkData.tabId === this.tabId;
        debugLog(`${LOG_PREFIX} Lock ${acquired ? 'acquired ✓' : 'failed (race condition)'}`);
        return acquired;
      }

      return false;
    } catch (err) {
      console.warn(`${LOG_PREFIX} localStorage error, proceeding anyway:`, err);
      // localStorage not available or other error
      return true; // Allow refresh anyway
    }
  }

  private releaseLock(): void {
    try {
      const existingLock = localStorage.getItem(LOCK_KEY);
      if (existingLock) {
        const lockData: LockData = JSON.parse(existingLock);
        if (lockData.tabId === this.tabId) {
          localStorage.removeItem(LOCK_KEY);
          debugLog(`${LOG_PREFIX} Lock released`);
        }
      }
    } catch {
      // Ignore errors
    }
  }

  /**
   * Broadcast a message to all other tabs
   */
  private broadcast(message: Omit<TokenRefreshMessage, 'tabId' | 'timestamp'>): void {
    const fullMessage: TokenRefreshMessage = {
      ...message,
      tabId: this.tabId,
      timestamp: Date.now(),
    };

    if (this.channel) {
      this.channel.postMessage(fullMessage);
    }
  }

  /**
   * Parse JWT token to get expiration time
   */
  public static getTokenExpiration(token: string): number | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const payload = JSON.parse(atob(parts[1]));
      if (payload.exp) {
        return payload.exp * 1000; // Convert to milliseconds
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Decode JWT token and log its contents (for debugging)
   */
  public static decodeAndLogToken(token: string, label = 'Token'): void {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        debugLog(`${LOG_PREFIX} ${label}: Invalid JWT format`);
        return;
      }

      const payload = JSON.parse(atob(parts[1]));
      const now = Date.now();
      const expMs = payload.exp ? payload.exp * 1000 : null;
      const iatMs = payload.iat ? payload.iat * 1000 : null;

      debugLog(`${LOG_PREFIX} ${label} decoded:`, {
        sub: payload.sub,
        role: payload.role,
        sessionId: payload.sessionId?.slice(-8),
        issuedAt: iatMs ? formatDate(iatMs) : 'unknown',
        expiresAt: expMs ? formatDate(expMs) : 'unknown',
        expiresIn: expMs ? formatMs(expMs - now) : 'unknown',
        isExpired: expMs ? now > expMs : 'unknown',
      });
    } catch (err) {
      debugLog(`${LOG_PREFIX} ${label}: Failed to decode`, err);
    }
  }

  /**
   * Schedule a refresh based on token expiration time
   */
  private scheduleRefresh(expirationMs: number): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    const now = Date.now();
    const refreshAt = expirationMs - REFRESH_BEFORE_EXPIRY_MS;
    const delay = Math.max(0, refreshAt - now);

    debugLog(
      `${LOG_PREFIX} Token expires at ${formatDate(expirationMs)} (in ${formatMs(
        expirationMs - now,
      )})`,
    );

    if (delay > 0) {
      debugLog(
        `${LOG_PREFIX} Scheduled refresh in ${formatMs(delay)} (at ${formatDate(now + delay)})`,
      );
      this.refreshTimer = setTimeout(() => {
        this.performRefresh();
      }, delay);
    } else if (expirationMs > now) {
      // Token is about to expire but not yet expired, refresh immediately
      debugLog(`${LOG_PREFIX} Token expiring soon, refreshing immediately`);
      this.performRefresh();
    } else {
      // Access token expired (e.g. timer throttled in a background tab), but
      // the refresh-token cookie may still be valid — try to recover
      debugLog(`${LOG_PREFIX} Token already expired, attempting recovery refresh`);
      this.performRefresh();
    }
  }

  /**
   * Schedule a retry after a failed refresh attempt
   */
  private scheduleRetry(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }
    debugLog(`${LOG_PREFIX} Scheduling refresh retry in ${formatMs(RETRY_DELAY_MS)}`);
    this.refreshTimer = setTimeout(() => {
      this.performRefresh();
    }, RETRY_DELAY_MS);
  }

  /**
   * Check current token and schedule refresh if needed
   */
  public checkAndScheduleRefresh(): void {
    const token = getAccessToken();
    if (!token) {
      // Cookie expired or was cleared — the refresh-token cookie may still
      // be valid, so attempt a refresh instead of giving up
      debugLog(`${LOG_PREFIX} No access token found in cookies, attempting refresh`);
      this.performRefresh();
      return;
    }

    TokenRefreshManager.decodeAndLogToken(token, 'Current token');

    const expirationMs = TokenRefreshManager.getTokenExpiration(token);
    if (expirationMs) {
      this.scheduleRefresh(expirationMs);
    } else {
      debugLog(`${LOG_PREFIX} Could not determine token expiration`);
    }
  }

  /**
   * Perform the actual token refresh
   */
  private async performRefresh(): Promise<void> {
    const now = Date.now();

    // Prevent rapid refresh attempts
    if (now - this.lastRefreshAttempt < this.minRefreshInterval) {
      debugLog(
        `${LOG_PREFIX} Throttled: last attempt was ${formatMs(now - this.lastRefreshAttempt)} ago`,
      );
      return;
    }

    if (this.isRefreshing) {
      debugLog(`${LOG_PREFIX} Already refreshing, skipping`);
      return;
    }

    debugLog(`${LOG_PREFIX} Attempting to refresh token...`);

    // Try to acquire lock
    if (!this.tryAcquireLock()) {
      debugLog(
        `${LOG_PREFIX} Could not acquire lock, will retry in ${formatMs(LOCK_TIMEOUT_MS)}`,
      );
      // Another tab is refreshing, wait and check later
      setTimeout(() => this.checkAndScheduleRefresh(), LOCK_TIMEOUT_MS);
      return;
    }

    this.isRefreshing = true;
    this.lastRefreshAttempt = now;

    // Broadcast that we're starting refresh
    this.broadcast({ type: 'REFRESH_STARTED' });
    debugLog(`${LOG_PREFIX} Starting refresh (this tab is the leader)`);

    try {
      if (this.refreshTokenFn) {
        const newToken = await this.refreshTokenFn();

        if (newToken) {
          debugLog(`${LOG_PREFIX} ✓ Token refreshed successfully`);
          TokenRefreshManager.decodeAndLogToken(newToken, 'New token');

          const expirationMs = TokenRefreshManager.getTokenExpiration(newToken);

          // Notify our own callback
          if (this.onTokenRefreshed) {
            this.onTokenRefreshed(newToken);
          }

          // Broadcast to other tabs
          this.broadcast({
            type: 'TOKEN_REFRESHED',
            accessToken: newToken,
            expirationMs: expirationMs || undefined,
          });
          debugLog(`${LOG_PREFIX} Broadcasted new token to other tabs`);

          // Schedule next refresh
          if (expirationMs) {
            this.scheduleRefresh(expirationMs);
          }
        } else {
          debugLog(`${LOG_PREFIX} ✗ Refresh returned no token`);
          this.scheduleRetry();
        }
      }
    } catch (error) {
      console.error(`${LOG_PREFIX} ✗ Token refresh failed:`, error);
      this.broadcast({ type: 'REFRESH_FAILED' });
      this.scheduleRetry();
    } finally {
      this.isRefreshing = false;
      this.releaseLock();
    }
  }

  /**
   * Initialize the manager with callbacks
   */
  public init(config: {
    onTokenRefreshed: TokenRefreshCallback;
    refreshTokenFn: () => Promise<string | null>;
  }): void {
    debugLog(`${LOG_PREFIX} Manager initialized with callbacks`);
    this.onTokenRefreshed = config.onTokenRefreshed;
    this.refreshTokenFn = config.refreshTokenFn;
    this.checkAndScheduleRefresh();
  }

  /**
   * Force a refresh (e.g., when WebSocket receives auth error).
   * Resolves once the attempt completes so callers can reconnect after it.
   */
  public forceRefresh(): Promise<void> {
    debugLog(`${LOG_PREFIX} Force refresh requested`);
    return this.performRefresh();
  }

  /**
   * Cleanup resources
   */
  public destroy(): void {
    debugLog(`${LOG_PREFIX} Destroying manager...`);
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }

    this.releaseLock();
    debugLog(`${LOG_PREFIX} Manager destroyed`);
  }
}

// Singleton instance
let managerInstance: TokenRefreshManager | null = null;

export function getTokenRefreshManager(): TokenRefreshManager {
  if (!managerInstance) {
    managerInstance = new TokenRefreshManager();
  }
  return managerInstance;
}

export function destroyTokenRefreshManager(): void {
  if (managerInstance) {
    managerInstance.destroy();
    managerInstance = null;
  }
}
